import { useEffect } from "react";

const UTM_PARAMS = [
  "utm_source",
  "utm_medium",
  "utm_campaign",
  "utm_term",
  "utm_content",
  "utm_feeditemid",
  "utm_device"
] as const;

const UTM_STORAGE_KEY = "_utm_tracking";

export interface UtmData {
  utm_source?: string;
  utm_medium?: string;
  utm_campaign?: string;
  utm_term?: string;
  utm_content?: string;
  utm_feeditemid?: string;
  utm_device?: string;
}

/**
 * Captures UTM parameters from the current URL and stores them in sessionStorage.
 * This mimics the behavior in comfreight-frontend where UTM params are captured
 * on page load and used during signup to track the user's source.
 */
export function useUtmTracking() {
  useEffect(() => {
    captureUtmParams();
  }, []);
}

/**
 * Captures UTM parameters from the current URL and stores them in sessionStorage.
 */
export function captureUtmParams(): void {
  try {
    const urlParams = new URLSearchParams(window.location.search);
    const utmData: UtmData = {};

    UTM_PARAMS.forEach((param) => {
      const value = urlParams.get(param);
      if (value) {
        utmData[param] = value;
      }
    });

    // Only store if we have UTM params
    if (Object.keys(utmData).length > 0) {
      sessionStorage.setItem(UTM_STORAGE_KEY, JSON.stringify(utmData));
    }
  } catch (error) {
    // console.warn("Failed to capture UTM params:", error);
  }
}

/**
 * Gets the stored UTM data from sessionStorage.
 */
export function getStoredUtmData(): UtmData | null {
  try {
    const stored = sessionStorage.getItem(UTM_STORAGE_KEY);
    if (stored) {
      return JSON.parse(stored);
    }
  } catch (error) {
    // console.warn("Failed to get stored UTM data:", error);
  }
  return null;
}

/**
 * Gets the signup source for API calls.
 * Priority: UTM source from URL > UTM source from storage > document.referrer > 'direct'
 */
export function getSignupSource(): string {
  // First check current URL params
  const urlParams = new URLSearchParams(window.location.search);
  const urlSource = urlParams.get("utm_source");
  if (urlSource) {
    return urlSource;
  }

  // Then check stored UTM data
  const storedData = getStoredUtmData();
  if (storedData?.utm_source) {
    return storedData.utm_source;
  }

  // Fall back to referrer origin (normalized to avoid leaking path/query) or 'direct'
  if (document.referrer) {
    try {
      const referrerUrl = new URL(document.referrer);
      return referrerUrl.origin || referrerUrl.host || "direct";
    } catch {
      // Invalid referrer URL, fall through to 'direct'
    }
  }

  return "direct";
}

export default useUtmTracking;
